import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AuthLabeledTextInput from "../../components/Inputs/AuthLabeledTextInput";
import { updateUser } from "../../store/reducers/currentUserSlice";
import axiosUsers from "../../services/axios/users";
import firebaseAuth from "../../services/firebase/firebaseAuth";
import SquareButton from "../Buttons/SquareButton";
import errors from "../../services/errors";

// Shared Auth Page
const AuthComponent = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [codeSent, setCodeSent] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [displayResendCode, setDisplayResendCode] = useState(false);
  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(props.phone ?? "");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize recaptcha
    firebaseAuth.getRecaptchaVerify();
  }, []);

  const handleError = (error) => {
    console.error("Authentication Error: ", error);
    if (error.code === "auth/invalid-phone-number") {
      setError("Invalid phone number, please enter a valid number");
    } else if (error.code === "auth/too-many-requests") {
      errors.report(error, {
        action: "AuthComponent:handleError",
        code: error.code,
        phoneNumber,
      });
      setError("Too many requests. Please try again later");
    } else if (error.message.includes("reCAPTCHA")) {
      setError(
        "There was a problem with the reCAPTCHA, please refresh the page and try again",
      );
    } else if (error.code === "auth/invalid-verification-code") {
      errors.report(error, {
        action: "AuthComponent:handleError",
        code: error.code,
        phoneNumber,
      });
      setError(
        "Invalid verification code, please enter the code that was sent to your phone",
      );
    } else if (
      error.code === "auth/code-expired" ||
      error.code === "auth/session-expired"
    ) {
      errors.report(error, {
        action: "AuthComponent:handleError",
        code: error.code,
        phoneNumber,
      });
      // Possible TODO
      // Clear verification code to have user send a new text. Will also likely need to reset the reCAPTCHA
      setError(
        "The verification code has expired. Please refresh and try again",
      );
    } else {
      errors.report(error, {
        action: "AuthComponent:handleError",
        code: error.code,
        phoneNumber,
      });
      setError("An unexpected error occurred, please try again");
    }
    setDisableButton(false);
  };

  const sendNumberToFirebase = () => {
    setLoading(true);
    setError("");
    setDisableButton(true);
    setDisplayResendCode(false);

    // TODO: Shouldn't force +1 since this will eventually be international
    // Remove dashes and parentheses and add +1 to the beginning
    const cleanedNumber = "+1" + ("" + phoneNumber).replace(/\D/g, "");

    firebaseAuth
      .sendSMS(
        cleanedNumber,
        () => {
          setCodeSent(true);
          setDisableButton(false);
        },
        (error) => {
          handleError(error);
        },
      )
      .finally(() => setLoading(false));

    setTimeout(() => {
      setDisplayResendCode(true);
    }, 10000);
  };

  const sendCodeToFirebase = async () => {
    setError("");
    setDisableButton(true);
    setLoading(true);

    try {
      // Send code to Firebase
      await firebaseAuth.signInWithCode(
        verificationCode,
        async (user) => {
          // Check if existing user or new user
          axiosUsers.getUserById({ uid: user.uid }, async (userData) => {
            if (userData) {
              // If existing user, set user in redux and complete login
              userData.currentCompanyId = Object.keys(userData.companies)[0];
              dispatch(updateUser(userData));
              navigate("/admin");
            } else {
              // If new user, navigate to AccountSetupPage
              navigate("/sign-up");
            }
          });
        },
        (error) => {
          handleError(error);

          setDisableButton(false);
        },
      );
    } catch (error) {
      // Handle other unexpected errors
      console.error("An unexpected error occurred: ", error);
      handleError(error);
      setDisableButton(false);
    } finally {
      setLoading(false);
    }
  };

  const resendSMS = () => {
    const cleanedNumber = "+1" + ("" + phoneNumber).replace(/\D/g, "");

    firebaseAuth.sendSMS(
      cleanedNumber,
      () => {},
      (error) => {
        handleError(error);
      },
    );
  };
  return (
    <div className="flex flex-1 w-full justify-center items-center">
      <div className="max-w-[500px] w-full">
        <div className="mb-3 text-center font-bold text-3xl text-full-white">
          {props.title}
        </div>
        {props.subtitle ? (
          <div className="text-center">{props.subtitle}</div>
        ) : null}
        {error ? (
          <div className="mb-4 text-center text-base text-snow-red">
            {error}
          </div>
        ) : null}
        <AuthLabeledTextInput
          disabled={false}
          inputMode="numeric"
          maxLength={10}
          numbersOnly={true}
          placeholder="Phone Number"
          setValue={setPhoneNumber}
          value={phoneNumber}
          className={codeSent ? "mb-0" : ""}
        />
        <div
          className="transition-all duration-300 ease-in-out overflow-hidden"
          style={{
            maxHeight: codeSent ? "200px" : "0",
            opacity: codeSent ? 1 : 0,
          }}
        >
          <AuthLabeledTextInput
            disabled={!codeSent}
            className={codeSent ? "mt-0" : ""}
            inputMode="numeric"
            maxLength={6}
            numbersOnly={true}
            placeholder={
              codeSent
                ? "Enter the code we just texted you"
                : "Verification Code"
            }
            setValue={setVerificationCode}
            value={verificationCode}
          />
        </div>
        <div className="w-full">
          <SquareButton
            label={codeSent ? "Login" : "Get Code"}
            color="blue"
            loading={loading}
            disabled={
              disableButton ||
              (phoneNumber.length < 10 && !codeSent) ||
              (codeSent && verificationCode.length < 6)
            }
            onClick={codeSent ? sendCodeToFirebase : sendNumberToFirebase}
          />
        </div>
        <div className="flex mt-0 justify-center items-center">
          {/* <div id="recaptcha-container"></div> */}
        </div>
        {displayResendCode && (
          <div
            className="my-6 text-center text-base text-[#0B6BAC] hover:opacity-80 active:opacity-40 cursor-pointer"
            onClick={resendSMS}
          >
            <div className="underline">Don't see our text message? </div>
            <span className="underline">Click here to resend code</span>
          </div>
        )}
        <div
          className="hover:opacity-80 active:opacity-40 mt-6 mb-6 text-center font-bold text-sm text-[#0B6BAC] cursor-pointer"
          onClick={() => {
            navigate(props.subtextUrl);
          }}
        >
          {props.subtext}
        </div>
      </div>
    </div>
  );
};

export default AuthComponent;
